import { useMemo } from 'react';

import { createSelector } from 'reselect';

import { IMarket } from '~/lib/data-contract';

import { useFrame } from '../utils/frame/hooks/useFrame';
import { useMarket } from '../utils/market/hooks/useMarket';

const formatPriceSelector = createSelector(
	[
		(value: number | undefined) => value,
		(_1: number | undefined, keepCurrencyCode: boolean) => keepCurrencyCode,
		(_1: number | undefined, _2: boolean, currency: string | undefined) => currency,
		(_1: number | undefined, _2: boolean, _3: string | undefined, locale: string) => locale,
	],
	(value, keepCurrencyCode, currency, locale = 'da-DK') => {
		if (typeof value !== 'number') return undefined;
		if (!currency) return String(value);

		if (keepCurrencyCode) {
			return Intl.NumberFormat(locale, {
				style: 'currency',
				currency,
			}).format(value);
		}

		const valueInParts = Intl.NumberFormat(locale, {
			style: 'currency',
			currency,
		}).formatToParts(value);

		return valueInParts
			.filter(({ type }) => type !== 'literal' && type !== 'currency')
			.map(({ value }) => value)
			.join('');
	},
);

export const useFormattedPrice = (value?: number, keepCurrencyCode = false) => {
	const {
		data: { market: frameMarket },
	} = useFrame();
	const { culture, market } = useMarket();

	const currency = frameMarket?.currency?.id;

	return useMemo(() => formatPriceSelector(value, keepCurrencyCode, currency, `${culture}-${market}`), [value, keepCurrencyCode, currency, culture, market]);
};

export const useFormattedPriceIfUnitMatchesCurrency = (value: number | undefined, unit: string, keepCurrencyCode = false) => {
	const {
		data: { market: frameMarket },
	} = useFrame();
	const { culture, market } = useMarket();

	const currency = frameMarket?.currency?.id;
	const locale = `${culture}-${market}`;

	return useMemo(() => {
		if (currency !== unit) {
			return value !== undefined ? Intl.NumberFormat(locale).format(value) : undefined;
		}
		return formatPriceSelector(value, keepCurrencyCode, currency, locale);
	}, [currency, keepCurrencyCode, locale, unit, value]);
};

type GetFormattedPriceProps = {
	value?: number;
	keepCurrencyCode?: boolean;
	locale: string;
	market: IMarket | undefined;
};
export const getFormattedPrice = ({ value, keepCurrencyCode = false, locale, market }: GetFormattedPriceProps) => formatPriceSelector(value, keepCurrencyCode, market?.currency?.id || 'DKK', locale);

type GetFormattedPriceIfUnitMatchesCurrencyProps = GetFormattedPriceProps & {
	unit: string;
};
export const getFormattedPriceIfUnitMatchesCurrency = (props: GetFormattedPriceIfUnitMatchesCurrencyProps) => {
	const { value = 0, keepCurrencyCode = false, locale, market, unit } = props;
	if (market?.currency?.id !== unit) return Intl.NumberFormat(locale).format(value);
	return getFormattedPrice({ value, keepCurrencyCode, locale, market });
};
